import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { useAppStateContext } from 'src/appState/AppState';
import { EventsCollectionList, EventsCollectionState, FilterKindType } from 'src/domains/sportsbook/shared/Types';
import { EventsTable, EventsTableHeaderStyle } from 'src/domains/sportsbook/shared/Components';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';

interface OutrightsConfigType {
    sports: Array<string>;
    filters: Array<FilterKindType>;
}

const OUTRIGHTS_CONFIG: OutrightsConfigType = {
    sports: ['golf', 'motorsport', 'cycling', 'pool', 'tvspecials', 'bowls', 'athletics'],
    filters: ['time', 'region', 'country'],
};

const isOutrightSport = (sport: string): boolean => OUTRIGHTS_CONFIG.sports.includes(sport);

const filtersSport: Array<FilterKindType> = ['competition', 'time', 'region', 'country'];
const filtersCollection: Array<FilterKindType> = ['time', 'region', 'country'];

interface TitleElementPropsType {
    collection: EventsCollectionList;
}

const TitleElement = observer('TitleElement', (props: TitleElementPropsType) => {
    const { collection } = props;
    const { total } = collection;
    return (
        <I18n
            langKey='events.table.header.total'
            defaultText='{total} events'
            params={{ total }}
        />
    );
});

const getFilters = (filters: Array<FilterKindType>, sport: string): Array<FilterKindType> => {
    if (sport === 'olympics') {
        return filters.filter((elem) => elem !== 'region' && elem !== 'country');
    }
    return filters;
};

interface SportEventsListInnerPropsType {
    sport: string;
    isAmericanFootball: boolean;
}

export const SportEventsList = observer('SportEventsList', (props: SportEventsListInnerPropsType) => {
    const { appSportsBookState } = useAppStateContext();
    const specialSportsState = appSportsBookState.specialSportsMap.get(props.sport);
    const common = useCommon();
    const config = ConfigComponents.get(common).config;

    const { sport, isAmericanFootball } = props;

    const specialExpandForSport = specialSportsState.specialExpandForSport;
    const collection =
        specialExpandForSport === null
            ? EventsCollectionState.get(common).listOfSport(sport)
            : specialSportsState.extendedCollection;
    return (
        <EventsTable
            headerStyle={EventsTableHeaderStyle.DOUBLE}
            filterVersion={config.filtersVersion.sportEvent}
            icon={null}
            key={sport}
            className='events-table--sport-list'
            title={<TitleElement collection={collection} />}
            isNotOutright={!isOutrightSport(sport)}
            filters={getFilters(filtersSport, sport)}
            showHeaderSport={false}
            allGroupsOpen={false}
            eventsCollection={collection}
            isTogglable={false}
            showPriceLegend={true}
            isLoading={collection.isLoading}
            isSportPage={true}
            isAmericanFootball={isAmericanFootball}
            sportId={props.sport}
        />
    );
});

interface SportCompetitionEventsListPropsType {
    sport: string;
    competition: CompetitionId;
    isCompetitionPage?: boolean;
}

export const SportCompetitionEventsList = observer(
    'SportCompetitionEventsList',
    (props: SportCompetitionEventsListPropsType) => {
        const common = useCommon();
        const config = ConfigComponents.get(common).config;
        const { sport, competition, isCompetitionPage } = props;
        const collection = EventsCollectionState.get(common).listOfSportAndCompetition(sport, competition.toOldId());
        return (
            <EventsTable
                headerStyle={EventsTableHeaderStyle.SOLID}
                filterVersion={
                    isCompetitionPage === true
                        ? config.filtersVersion.competitionPage
                        : config.filtersVersion.sportEvent
                }
                icon={null}
                className='events-table--sport-list'
                title={<TitleElement collection={collection} />}
                isNotOutright={!isOutrightSport(sport)}
                filters={getFilters(filtersCollection, sport)}
                showHeaderSport={false}
                allGroupsOpen={false}
                eventsCollection={collection}
                isTogglable={false}
                showPriceLegend={true}
                isLoading={collection.isLoading}
                isCompetitionPage={isCompetitionPage}
                isAmericanFootball={false}
                sportId={sport}
            />
        );
    }
);
